import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LoginArea } from '@/components/auth/LoginArea';
import { WalletModal } from '@/components/WalletModal';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { MobileNav } from '@/components/MobileNav';
import { PwaInstallPrompt } from '@/components/PwaInstallPrompt';
import { ADMIN_PUBKEY, useAdminConfig, useAdminAnalytics } from '@/hooks/useAdminConfig';

/* ── SVG icons ─────────────────────────────────────────────────────────── */
function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
    </svg>
  );
}

function HomeIcon({ filled }: { filled?: boolean }) {
  return filled ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" /></svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12L12 3l9 9M5 10v10h5v-5h4v5h5V10" /></svg>
  );
}

function TrendingIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" /></svg>;
}

function LiveIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="4" fill="#f44336" />
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
    </svg>
  );
}

function FollowingIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 8H4V6h16v2zm-2-6H6v2h12V2zm4 10v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-8c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2zm-6 4l-6-3.27v6.53L16 16z" /></svg>;
}

function WalletIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 18v1c0 1.1-.9 2-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
    </svg>
  );
}

function BitcoinIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.06 11.57c.59-.69.94-1.59.94-2.57 0-1.86-1.27-3.43-3-3.87V3h-2v2h-2V3H9v2H6v2h1c.55 0 1 .45 1 1v8c0 .55-.45 1-1 1H6v2h3v2h2v-2h2v2h2v-2.13c2.39-.54 4-2.65 4-5.07 0-1.37-.53-2.62-1.4-3.55M10 7h4c1.1 0 2 .9 2 2s-.9 2-2 2h-4V7zm5 10h-5v-4h5c1.1 0 2 .9 2 2s-.9 2-2 2z" /></svg>;
}

function NostrIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M7 2v11h3v9l7-12h-4l4-8z" /></svg>;
}

function TechIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" /></svg>;
}

function GamingIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M15 7.5V2H9v5.5l3 3 3-3zM7.5 9H2v6h5.5l3-3-3-3zM9 16.5V22h6v-5.5l-3-3-3 3zM16.5 9l-3 3 3 3H22V9h-5.5z" /></svg>;
}

function MusicIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" /></svg>;
}

function PodcastIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" /></svg>;
}

function TutorialIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>;
}

function SettingsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
    </svg>
  );
}

function NotificationsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
  );
}

/* ── Nav data ──────────────────────────────────────────────────────────── */
const NAV_ITEMS = [
  { label: 'Home',          href: '/',               icon: (a: boolean) => <HomeIcon filled={a} /> },
  { label: 'Live',          href: '/live',           icon: () => <LiveIcon /> },
  { label: 'Trending',      href: '/trending',       icon: () => <TrendingIcon /> },
  { label: 'Following',     href: '/following',      icon: () => <FollowingIcon /> },
  { label: 'Notifications', href: '/notifications',  icon: () => <NotificationsIcon /> },
];

const TOPIC_ITEMS = [
  { label: 'Bitcoin',    href: '/tag/bitcoin',  icon: <BitcoinIcon /> },
  { label: 'Nostr',      href: '/tag/nostr',    icon: <NostrIcon /> },
  { label: 'Technology', href: '/tag/tech',     icon: <TechIcon /> },
  { label: 'Gaming',     href: '/tag/gaming',   icon: <GamingIcon /> },
  { label: 'Podcasts',   href: '/tag/podcast',  icon: <PodcastIcon /> },
  { label: 'Music',      href: '/tag/music',    icon: <MusicIcon /> },
  { label: 'Tutorials',  href: '/tag/tutorial', icon: <TutorialIcon /> },
];

function AdminIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
    </svg>
  );
}

/* ── Component ─────────────────────────────────────────────────────────── */
interface LayoutProps {
  children: React.ReactNode;
  onUploadClick?: () => void;
  onGoLiveClick?: () => void;
}

export function Layout({ children, onUploadClick, onGoLiveClick }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, metadata } = useCurrentUser();
  const { config: adminConfig } = useAdminConfig();
  const { recordPageView, recordVisitor } = useAdminAnalytics();
  const isAdmin = user?.pubkey === ADMIN_PUBKEY;

  // Record page view and visitor on route change
  useEffect(() => {
    recordPageView();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.pubkey) recordVisitor(user.pubkey);
  }, [user?.pubkey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close sidebar on mobile navigation
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowMobileSearch(false);
    }
  };

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const displayName = metadata?.name ?? metadata?.display_name ?? '';

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>

      {/* ── Maintenance banner ──────────────────────────────────────────── */}
      {adminConfig.maintenanceMessage && (
        <div
          className="w-full px-4 py-2 text-center text-sm font-medium z-[200] relative"
          style={{ background: '#f7931a', color: '#fff' }}
        >
          {adminConfig.maintenanceMessage}
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header
        className="fixed top-0 left-0 right-0 z-[100] flex flex-col"
        style={{
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--gb-border)',
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        {/* Main header row */}
        <div className="flex items-center gap-3 px-3 h-[56px] sm:h-[60px] max-w-[1400px] mx-auto w-full">

          {/* Hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition-colors"
            style={{ color: 'var(--text-primary)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
            aria-label="Toggle menu"
          >
            <MenuIcon />
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 no-underline">
            <img
              src="/images/goblinboxlogo.png"
              alt="Goblinbox"
              style={{ height: 36, width: 'auto', display: 'block' }}
            />
          </Link>

          {/* Desktop search */}
          {!showMobileSearch && (
            <form onSubmit={handleSearch} className="hidden sm:flex flex-1 max-w-[600px] mx-auto">
              <div
                className="flex w-full overflow-hidden"
                style={{ borderRadius: 20, border: '1px solid var(--gb-border)', background: 'var(--bg-primary)' }}
              >
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search videos..."
                  className="border-0 bg-transparent rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 px-4"
                  style={{ color: 'var(--text-primary)' }}
                />
                <button
                  type="submit"
                  className="px-4 flex items-center transition-colors"
                  style={{ background: 'var(--bg-secondary)', borderLeft: '1px solid var(--gb-border)', color: 'var(--text-secondary)' }}
                >
                  <SearchIcon />
                </button>
              </div>
            </form>
          )}

          {/* Mobile search expanded */}
          {showMobileSearch && (
            <form onSubmit={handleSearch} className="flex sm:hidden flex-1">
              <div
                className="flex w-full overflow-hidden"
                style={{ borderRadius: 20, border: '1px solid var(--gb-border)', background: 'var(--bg-primary)' }}
              >
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  autoFocus
                  className="border-0 bg-transparent rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3 text-sm"
                  style={{ color: 'var(--text-primary)' }}
                />
                <button
                  type="button"
                  onClick={() => setShowMobileSearch(false)}
                  className="px-3 text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  ✕
                </button>
              </div>
            </form>
          )}

          {/* Right actions */}
          <div className="flex items-center gap-2 ml-auto shrink-0">
            {/* Mobile search toggle */}
            {!showMobileSearch && (
              <button
                onClick={() => setShowMobileSearch(true)}
                className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                style={{ color: 'var(--text-primary)' }}
                aria-label="Search"
              >
                <SearchIcon />
              </button>
            )}

            {/* Lightning Wallet */}
            {user && (
              <WalletModal>
                <button
                  className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                  style={{ color: 'var(--text-primary)' }}
                  aria-label="Lightning Wallet"
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <WalletIcon />
                </button>
              </WalletModal>
            )}

            {/* Go Live */}
            {user && (
              <button
                onClick={onGoLiveClick}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded font-medium text-sm text-white transition-all hover:opacity-90"
                style={{ background: '#f44336' }}
              >
                <LiveIcon />
                <span>Go Live</span>
              </button>
            )}

            {/* Upload */}
            {user && (
              <button
                onClick={onUploadClick}
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded font-medium text-sm text-white transition-all hover:opacity-90"
                style={{ background: '#1da1f2' }}
              >
                <UploadIcon />
                <span>Create</span>
              </button>
            )}

            {/* Auth / Avatar */}
            {user ? (
              <Link to={`/profile/${user.pubkey}`}>
                <Avatar className="h-8 w-8 cursor-pointer" style={{ border: '2px solid var(--gb-accent)' }}>
                  <AvatarImage src={metadata?.picture} />
                  <AvatarFallback
                    className="text-xs font-bold"
                    style={{ background: 'var(--gb-accent)', color: '#fff' }}
                  >
                    {displayName[0]?.toUpperCase() ?? '?'}
                  </AvatarFallback>
                </Avatar>
              </Link>
            ) : (
              <LoginArea className="max-w-[120px]" />
            )}
          </div>
        </div>

      </header>

      {/* ── Sidebar overlay backdrop ────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex pt-[56px] sm:pt-[60px]">

        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <aside
          className={cn(
            'fixed top-0 left-0 bottom-0 z-[101] flex flex-col overflow-hidden transition-all duration-200',
            // Desktop: always visible at 72px, expand on open
            'hidden md:flex',
            sidebarOpen ? 'w-[240px] shadow-[4px_0_10px_rgba(0,0,0,0.4)]' : 'w-[72px]',
          )}
          style={{
            background: 'var(--bg-primary)',
            borderRight: '1px solid var(--gb-border)',
            top: sidebarOpen ? 0 : 60,
            height: sidebarOpen ? '100dvh' : 'calc(100dvh - 60px)',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.2) transparent',
          }}
        >
          {/* Expanded header (close button only) */}
          {sidebarOpen && (
            <div
              className="flex items-center px-4 h-[60px] shrink-0 sticky top-0 z-10"
              style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--gb-border)' }}
            >
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                style={{ color: 'var(--text-primary)', background: 'transparent' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <MenuIcon />
              </button>
            </div>
          )}

          <nav className="flex flex-col py-2 flex-1 min-h-0">
            {/* Main nav */}
            {NAV_ITEMS.map(item => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  title={!sidebarOpen ? item.label : undefined}
                  className={cn(
                    'flex items-center rounded-lg mb-1 transition-colors no-underline',
                    sidebarOpen ? 'justify-start px-4 py-3 mx-2' : 'justify-center p-3 mx-1',
                    active
                      ? 'font-medium'
                      : '',
                  )}
                  style={{
                    color: 'var(--text-primary)',
                    background: active ? 'var(--bg-secondary)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="shrink-0">{item.icon(active)}</span>
                  {sidebarOpen && <span className="ml-3 text-sm whitespace-nowrap">{item.label}</span>}
                </Link>
              );
            })}

            {/* Divider */}
            <div className="my-2" style={{ borderTop: '1px solid var(--gb-border)', margin: '0.5rem 0.75rem' }} />

            {/* Topics section title */}
            {sidebarOpen && (
              <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                Topics
              </p>
            )}

            {TOPIC_ITEMS.map(item => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  title={!sidebarOpen ? item.label : undefined}
                  className={cn(
                    'flex items-center rounded-lg mb-1 transition-colors no-underline',
                    sidebarOpen ? 'justify-start px-4 py-3 mx-2' : 'justify-center p-3 mx-1',
                  )}
                  style={{
                    color: 'var(--text-primary)',
                    background: active ? 'var(--bg-secondary)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="shrink-0">{item.icon}</span>
                  {sidebarOpen && <span className="ml-3 text-sm whitespace-nowrap">{item.label}</span>}
                </Link>
              );
            })}

            {/* Go Live + Upload in sidebar */}
            {user && sidebarOpen && (
              <>
                <div className="my-2" style={{ borderTop: '1px solid var(--gb-border)', margin: '0.5rem 0.75rem' }} />
                <button
                  onClick={() => { setSidebarOpen(false); onGoLiveClick?.(); }}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg text-sm transition-colors"
                  style={{ color: '#f44336', background: 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <LiveIcon />
                  <span className="ml-3 whitespace-nowrap font-semibold">Go Live</span>
                </button>
                <button
                  onClick={() => { setSidebarOpen(false); onUploadClick?.(); }}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg text-sm transition-colors"
                  style={{ color: 'var(--text-primary)', background: 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <UploadIcon />
                  <span className="ml-3 whitespace-nowrap">Upload Video</span>
                </button>
              </>
            )}

            {/* Settings link — pinned to bottom of sidebar */}
            <div className="mt-auto pt-2" style={{ borderTop: '1px solid var(--gb-border)', margin: '0.5rem 0 0' }}>
              {(() => {
                const active = isActive('/settings');
                return (
                  <Link
                    to="/settings"
                    onClick={() => setSidebarOpen(false)}
                    title={!sidebarOpen ? 'Settings' : undefined}
                    className={cn(
                      'flex items-center rounded-lg mb-1 transition-colors no-underline',
                      sidebarOpen ? 'justify-start px-4 py-3 mx-2' : 'justify-center p-3 mx-1',
                      active ? 'font-medium' : '',
                    )}
                    style={{
                      color: 'var(--text-primary)',
                      background: active ? 'var(--bg-secondary)' : 'transparent',
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span className="shrink-0"><SettingsIcon /></span>
                    {sidebarOpen && <span className="ml-3 text-sm whitespace-nowrap">Settings</span>}
                  </Link>
                );
              })()}
              {/* Admin link — only visible to the admin pubkey */}
              {isAdmin && (() => {
                const active = isActive('/admin');
                return (
                  <Link
                    to="/admin"
                    onClick={() => setSidebarOpen(false)}
                    title={!sidebarOpen ? 'Admin' : undefined}
                    className={cn(
                      'flex items-center rounded-lg mb-1 transition-colors no-underline',
                      sidebarOpen ? 'justify-start px-4 py-3 mx-2' : 'justify-center p-3 mx-1',
                      active ? 'font-medium' : '',
                    )}
                    style={{
                      color: '#3a9e3a',
                      background: active ? 'rgba(58,158,58,0.12)' : 'transparent',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(58,158,58,0.12)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span className="shrink-0"><AdminIcon /></span>
                    {sidebarOpen && <span className="ml-3 text-sm whitespace-nowrap font-semibold">Admin</span>}
                  </Link>
                );
              })()}
            </div>

            {/* Footer */}
            {sidebarOpen && (
              <div className="px-4 pb-8 pt-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2">
                  <Link to="/about" className="hover:underline" style={{ color: 'var(--text-secondary)' }}>About</Link>
                  <Link to="/faq" className="hover:underline" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
                  <Link to="/terms" className="hover:underline" style={{ color: 'var(--text-secondary)' }}>Terms</Link>
                  <Link to="/privacy" className="hover:underline" style={{ color: 'var(--text-secondary)' }}>Privacy</Link>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* ── Mobile sidebar (full-width drawer) ─────────────────────── */}
        <aside
          className={cn(
            'fixed top-0 left-0 bottom-0 z-[101] flex flex-col md:hidden transition-transform duration-200',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
          style={{
            width: 280,
            height: '100dvh',
            background: 'var(--bg-primary)',
            borderRight: '1px solid var(--gb-border)',
            boxShadow: '4px 0 10px rgba(0,0,0,0.4)',
            overflowY: 'auto',
          }}
        >
          {/* Mobile sidebar header */}
          <div
            className="flex items-center px-4 h-[60px] shrink-0 sticky top-0 z-10"
            style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--gb-border)' }}
          >
            <button
              onClick={() => setSidebarOpen(false)}
              className="flex items-center justify-center w-10 h-10 rounded-full"
              style={{ color: 'var(--text-primary)' }}
            >
              <MenuIcon />
            </button>
          </div>

          <nav className="flex flex-col py-2 flex-1">
            {NAV_ITEMS.map(item => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg mb-1 text-sm no-underline transition-colors"
                  style={{
                    color: 'var(--text-primary)',
                    background: active ? 'var(--bg-secondary)' : 'transparent',
                    fontWeight: active ? '500' : '400',
                  }}
                >
                  <span className="shrink-0">{item.icon(active)}</span>
                  <span className="ml-3">{item.label}</span>
                </Link>
              );
            })}

            <div className="my-2" style={{ borderTop: '1px solid var(--gb-border)', margin: '0.5rem 1rem' }} />

            <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              Topics
            </p>

            {TOPIC_ITEMS.map(item => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg mb-1 text-sm no-underline transition-colors"
                  style={{
                    color: 'var(--text-primary)',
                    background: active ? 'var(--bg-secondary)' : 'transparent',
                  }}
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span className="ml-3">{item.label}</span>
                </Link>
              );
            })}

            {user && (
              <>
                <div className="my-2" style={{ borderTop: '1px solid var(--gb-border)', margin: '0.5rem 1rem' }} />
                <button
                  onClick={() => { setSidebarOpen(false); onGoLiveClick?.(); }}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg text-sm font-semibold"
                  style={{ color: '#f44336' }}
                >
                  <LiveIcon />
                  <span className="ml-3">Go Live</span>
                </button>
                <button
                  onClick={() => { setSidebarOpen(false); onUploadClick?.(); }}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg text-sm"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <UploadIcon />
                  <span className="ml-3">Upload Video</span>
                </button>
                <WalletModal>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center w-full px-4 py-3 mx-2 rounded-lg text-sm"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <WalletIcon />
                    <span className="ml-3">Lightning Wallet</span>
                  </button>
                </WalletModal>
              </>
            )}

            {/* Settings — mobile sidebar */}
            <div className="mt-auto" style={{ borderTop: '1px solid var(--gb-border)', margin: '0.5rem 0 0' }}>
              <Link
                to="/settings"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center px-4 py-3 mx-2 rounded-lg mb-1 text-sm no-underline transition-colors"
                style={{
                  color: 'var(--text-primary)',
                  background: isActive('/settings') ? 'var(--bg-secondary)' : 'transparent',
                  fontWeight: isActive('/settings') ? '500' : '400',
                }}
              >
                <span className="shrink-0"><SettingsIcon /></span>
                <span className="ml-3">Settings</span>
              </Link>
              {/* Admin link — mobile, only visible to admin pubkey */}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center px-4 py-3 mx-2 rounded-lg mb-1 text-sm no-underline font-semibold"
                  style={{
                    color: '#3a9e3a',
                    background: isActive('/admin') ? 'rgba(58,158,58,0.12)' : 'transparent',
                  }}
                >
                  <span className="shrink-0"><AdminIcon /></span>
                  <span className="ml-3">Admin</span>
                </Link>
              )}
            </div>

            <div
              className="px-4 pt-2 text-xs"
              style={{
                color: 'var(--text-secondary)',
                paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))',
              }}
            />
          </nav>
        </aside>

        {/* ── Main content ────────────────────────────────────────────── */}
        <main
          className={cn(
            'flex-1 min-w-0 transition-all duration-200',
            // On desktop, push right by sidebar width
            sidebarOpen ? 'md:ml-[240px]' : 'md:ml-[72px]',
          )}
          style={{ paddingTop: 0 }}
        >
          {children}
        </main>
      </div>

      {/* ── Mobile bottom navigation ────────────────────────────────────── */}
      <MobileNav onUploadClick={onUploadClick} />

      {/* ── PWA install prompt ──────────────────────────────────────────── */}
      <PwaInstallPrompt />
    </div>
  );
}
